import { useCallback, useEffect, useState } from "react";
import { useNavigate, useParams, Link } from "react-router-dom";
import { format } from "date-fns";
import { ArrowLeft, FileText, CheckCircle2, XCircle, Check, Plus, Circle, CheckCircle } from "lucide-react";
import { toast } from "sonner";
import api, { formatApiError, fileUrl } from "../lib/api";
import { TypeBadge, SeverityBadge, ConfidenceMeter, EVENT_TYPES } from "../components/Badges";

const EventDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [event, setEvent] = useState(null);
  const [newType, setNewType] = useState("");
  const [taskTitle, setTaskTitle] = useState("");
  const [taskDue, setTaskDue] = useState("");
  const [applying, setApplying] = useState(false);

  const load = useCallback(async () => {
    try {
      const r = await api.get(`/events/${id}`);
      setEvent(r.data);
      setNewType(r.data.event_type);
    } catch (e) {
      toast.error(formatApiError(e));
      navigate("/events");
    }
  }, [id, navigate]);

  useEffect(() => {
    load();
  }, [load]);

  if (!event) {
    return (
      <div className="flex h-64 items-center justify-center" data-testid="event-detail-loading">
        <div className="h-8 w-8 rounded-full border-2 border-emerald-500 dark:border-emerald-500/60 border-t-transparent animate-spin" />
      </div>
    );
  }

  const doAction = async (fn, msg) => {
    try {
      await fn();
      toast.success(msg);
      load();
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  const addTask = async (e) => {
    e.preventDefault();
    if (!taskTitle.trim()) return;
    await doAction(
      () => api.post(`/events/${id}/tasks`, { title: taskTitle.trim(), due_date: taskDue || null }),
      "Task added"
    );
    setTaskTitle("");
    setTaskDue("");
  };

  const src = event.source || {};
  const rawArticles = (event.extracted || {}).articles;
  const articles = (Array.isArray(rawArticles) ? rawArticles : []).filter((a) => a && (a.article_no || a.name || a.price != null));
  const extractedEntries = Object.entries(event.extracted || {}).filter(
    ([k, v]) => k !== "articles" && v !== null && v !== "" && v !== undefined
  );

  const applyPrices = async () => {
    setApplying(true);
    try {
      const r = await api.post(`/events/${id}/apply-prices`);
      toast.success(`Applied ${r.data.applied} articles to catalog (${r.data.price_changes_logged} price changes logged)`);
      load();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setApplying(false);
    }
  };

  return (
    <div data-testid="event-detail-page" className="max-w-3xl space-y-5">
      <button
        data-testid="event-back-btn"
        onClick={() => navigate(-1)}
        className="inline-flex items-center gap-1.5 text-base text-slate-600 dark:text-slate-400 hover:text-emerald-600 dark:hover:text-emerald-400 transition-colors"
      >
        <ArrowLeft className="h-4 w-4" /> Back
      </button>

      <div className="rounded-lg border border-slate-300 dark:border-slate-700 bg-white dark:bg-slate-900 p-5 space-y-4">
        <div className="flex flex-wrap items-center gap-2">
          <TypeBadge type={event.event_type} />
          <SeverityBadge severity={event.severity} note={event.impact_note} />
          {event.needs_confirm && (
            <span data-testid="detail-needs-confirm-chip" className="rounded-md border border-amber-400 dark:border-amber-500/50 bg-amber-50 dark:bg-amber-500/10 px-2 py-0.5 text-sm font-mono font-semibold text-amber-700 dark:text-amber-300">
              Needs your confirm
            </span>
          )}
          <span className="ml-auto text-sm font-mono text-slate-600 dark:text-slate-400">
            {format(new Date(event.created_at), "d MMM yyyy, HH:mm")}
          </span>
        </div>

        <div>
          <h1 className="text-xl md:text-2xl font-bold tracking-tight text-slate-900 dark:text-slate-100">{event.title}</h1>
          <Link
            to={event.vendor_id ? `/vendors/${event.vendor_id}` : "/vendors"}
            data-testid="detail-vendor-link"
            className="mt-1 inline-block text-base font-mono uppercase tracking-wider text-cyan-600 dark:text-cyan-400 hover:text-cyan-700 dark:hover:text-cyan-300"
          >
            {event.vendor_name}
          </Link>
        </div>

        <p className="text-base text-slate-700 dark:text-slate-300 leading-relaxed">{event.summary}</p>

        <div className="grid grid-cols-2 md:grid-cols-4 gap-3 text-base">
          <div className="rounded-md border border-slate-300 dark:border-slate-700 bg-slate-50 dark:bg-slate-800/60 p-3">
            <div className="text-sm font-mono uppercase tracking-widest text-slate-600 dark:text-slate-400">Confidence</div>
            <div className="mt-1"><ConfidenceMeter value={event.confidence} /></div>
          </div>
          <div className="rounded-md border border-slate-300 dark:border-slate-700 bg-slate-50 dark:bg-slate-800/60 p-3">
            <div className="text-sm font-mono uppercase tracking-widest text-slate-600 dark:text-slate-400">Impact area</div>
            <div className="mt-1 font-semibold text-slate-800 dark:text-slate-200">{event.impact_area}</div>
          </div>
          <div className="rounded-md border border-slate-300 dark:border-slate-700 bg-slate-50 dark:bg-slate-800/60 p-3">
            <div className="text-sm font-mono uppercase tracking-widest text-slate-600 dark:text-slate-400">Due date</div>
            <div className="mt-1 font-semibold text-slate-800 dark:text-slate-200">{event.due_date || "—"}</div>
          </div>
          <div className="rounded-md border border-slate-300 dark:border-slate-700 bg-slate-50 dark:bg-slate-800/60 p-3">
            <div className="text-sm font-mono uppercase tracking-widest text-slate-600 dark:text-slate-400">Status</div>
            <div className="mt-1 font-semibold capitalize text-slate-800 dark:text-slate-200">{event.status}</div>
          </div>
        </div>

        {event.recommended_action && (
          <div className="rounded-md border border-emerald-300 dark:border-emerald-500/40 bg-emerald-50 dark:bg-emerald-500/10 px-4 py-3">
            <div className="text-sm font-mono uppercase tracking-widest text-emerald-600 dark:text-emerald-400 mb-1">Recommended action</div>
            <p className="text-base text-slate-800 dark:text-slate-200">{event.recommended_action}</p>
          </div>
        )}

        {articles.length > 0 && (
          <div className="rounded-md border border-amber-300 dark:border-amber-500/40 bg-amber-50 dark:bg-amber-500/10 p-4" data-testid="extracted-price-list">
            <div className="flex flex-wrap items-center justify-between gap-2 mb-2">
              <div className="text-sm font-mono uppercase tracking-widest text-amber-600 dark:text-amber-400">
                Extracted price list — {articles.length} item{articles.length > 1 ? "s" : ""} read from photos
              </div>
              <button
                data-testid="apply-prices-btn"
                onClick={applyPrices}
                disabled={applying || event.prices_applied}
                className="inline-flex items-center gap-1.5 rounded-md bg-emerald-500 px-3 py-1.5 text-sm font-bold text-emerald-950 hover:bg-emerald-400 transition-colors disabled:opacity-50"
              >
                {event.prices_applied ? "Applied to catalog ✓" : applying ? "Applying…" : "Apply prices to catalog"}
              </button>
            </div>
            <div className="overflow-x-auto">
              <table className="w-full text-base">
                <thead>
                  <tr className="border-b border-slate-300 dark:border-slate-700 text-left text-sm font-mono uppercase tracking-widest text-slate-600 dark:text-slate-400">
                    <th className="py-2 pr-3">Photo</th>
                    <th className="py-2 pr-3">Article</th>
                    <th className="py-2 pr-3">Name</th>
                    <th className="py-2 pr-3">Size</th>
                    <th className="py-2 pr-3 text-right">Price</th>
                    <th className="py-2 text-right">MRP</th>
                  </tr>
                </thead>
                <tbody>
                  {articles.map((a, i) => (
                    <tr key={i} data-testid={`price-row-${i}`} className="border-b border-slate-300 dark:border-slate-700">
                      <td className="py-2 pr-3">
                        {a.image_path ? (
                          <a href={fileUrl(a.image_path)} target="_blank" rel="noopener noreferrer" data-testid={`price-photo-link-${i}`}>
                            <img
                              src={fileUrl(a.image_path)}
                              alt={a.article_no || a.name || "article photo"}
                              data-testid={`price-photo-${i}`}
                              className="h-16 w-16 rounded-md object-cover border border-slate-300 dark:border-slate-700"
                            />
                          </a>
                        ) : (
                          <span className="text-slate-600 dark:text-slate-400">—</span>
                        )}
                      </td>
                      <td className="py-2 pr-3 font-mono text-cyan-600 dark:text-cyan-400">{a.article_no || "—"}</td>
                      <td className="py-2 pr-3 text-slate-700 dark:text-slate-300">{a.name || "—"}{a.color ? ` (${a.color})` : ""}</td>
                      <td className="py-2 pr-3 font-mono text-slate-600 dark:text-slate-400">{a.size || "—"}</td>
                      <td className="py-2 pr-3 text-right font-mono text-emerald-600 dark:text-emerald-400">{a.price != null ? `₹${a.price}` : "?"}</td>
                      <td className="py-2 text-right font-mono text-slate-600 dark:text-slate-400">{a.mrp != null ? `₹${a.mrp}` : "—"}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {extractedEntries.length > 0 && (
          <div className="rounded-md border border-slate-300 dark:border-slate-700 bg-slate-50 dark:bg-slate-800/60 p-4">
            <div className="text-sm font-mono uppercase tracking-widest text-slate-600 dark:text-slate-400 mb-2">Extracted details</div>
            <dl className="grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-1.5 text-base">
              {extractedEntries.map(([k, v]) => (
                <div key={k} className="flex justify-between gap-3">
                  <dt className="text-slate-600 dark:text-slate-400 font-mono text-sm uppercase tracking-wide">{k.replace(/_/g, " ")}</dt>
                  <dd className="text-slate-700 dark:text-slate-300 text-right break-all">{typeof v === "object" ? JSON.stringify(v) : String(v)}</dd>
                </div>
              ))}
            </dl>
          </div>
        )}

        <div className="rounded-md border border-slate-300 dark:border-slate-700 bg-slate-50 dark:bg-slate-800/60 p-4">
          <div className="flex items-center justify-between mb-2">
            <div className="text-sm font-mono uppercase tracking-widest text-slate-600 dark:text-slate-400">Source</div>
            {src.storage_path && (
              <a
                data-testid="detail-source-doc-btn"
                href={fileUrl(src.storage_path)}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-1.5 rounded-md border border-indigo-300 dark:border-indigo-500/40 bg-indigo-50 dark:bg-indigo-500/10 px-2.5 py-1 text-sm font-semibold text-indigo-700 dark:text-indigo-300 hover:bg-indigo-100 dark:hover:bg-indigo-500/15 transition-colors"
              >
                <FileText className="h-3.5 w-3.5" /> {src.filename || "Open source doc"}
              </a>
            )}
          </div>
          {src.excerpt && <p className="text-sm text-slate-600 dark:text-slate-400 italic leading-relaxed whitespace-pre-wrap">{src.excerpt}</p>}
          {(src.page_paths || []).length > 0 && (
            <div className="mt-3" data-testid="source-pages-gallery">
              <div className="text-sm font-mono uppercase tracking-widest text-slate-600 dark:text-slate-400 mb-2">
                PDF pages ({src.page_paths.length})
              </div>
              <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
                {src.page_paths.map((p, i) => (
                  <a key={p} href={fileUrl(p)} target="_blank" rel="noopener noreferrer" data-testid={`source-page-link-${i}`}>
                    <img
                      src={fileUrl(p)}
                      alt={`Page ${i + 1}`}
                      data-testid={`source-page-${i}`}
                      className="w-full rounded-md border border-slate-300 dark:border-slate-700 hover:border-emerald-500 transition-colors"
                    />
                  </a>
                ))}
              </div>
            </div>
          )}
        </div>

        <div className="flex flex-wrap gap-2 pt-1" data-testid="detail-actions">
          {event.needs_confirm && (
            <div className="flex w-full flex-wrap items-center gap-2 rounded-md border border-amber-300 dark:border-amber-500/40 bg-amber-50 dark:bg-amber-500/10 p-3">
              <select
                data-testid="detail-reclassify-select"
                value={newType}
                onChange={(e) => setNewType(e.target.value)}
                className="rounded-md border border-slate-300 dark:border-slate-700 bg-white dark:bg-slate-900 px-2.5 py-1.5 text-sm text-slate-700 dark:text-slate-300 focus:outline-none"
              >
                {EVENT_TYPES.map((t) => (
                  <option key={t} value={t}>{t}</option>
                ))}
              </select>
              <button
                data-testid="detail-confirm-btn"
                onClick={() => doAction(() => api.post(`/events/${id}/confirm`, { event_type: newType }), "Event confirmed — vendor memory updated")}
                className="inline-flex items-center gap-1.5 rounded-md bg-emerald-500 px-3 py-1.5 text-sm font-bold text-emerald-950 hover:bg-emerald-400 transition-colors"
              >
                <CheckCircle2 className="h-3.5 w-3.5" /> Confirm
              </button>
              <button
                data-testid="detail-reject-btn"
                onClick={() => doAction(() => api.post(`/events/${id}/reject`), "Event rejected")}
                className="inline-flex items-center gap-1.5 rounded-md border border-slate-300 dark:border-slate-700 px-3 py-1.5 text-sm font-semibold text-slate-700 dark:text-slate-300 hover:bg-slate-200 dark:hover:bg-slate-700 transition-colors"
              >
                <XCircle className="h-3.5 w-3.5" /> Reject
              </button>
            </div>
          )}
          {!event.needs_confirm && event.status === "active" && (
            <button
              data-testid="detail-acknowledge-btn"
              onClick={() => doAction(() => api.post(`/events/${id}/acknowledge`), "Event acknowledged")}
              className="inline-flex items-center gap-1.5 rounded-md border border-slate-300 dark:border-slate-700 px-3 py-1.5 text-sm font-semibold text-slate-700 dark:text-slate-300 hover:bg-slate-200 dark:hover:bg-slate-700 transition-colors"
            >
              <Check className="h-3.5 w-3.5" /> Acknowledge
            </button>
          )}
        </div>
      </div>

      <div className="rounded-lg border border-slate-300 dark:border-slate-700 bg-white dark:bg-slate-900 p-5" data-testid="detail-tasks-section">
        <h2 className="text-base font-semibold text-slate-800 dark:text-slate-200 mb-3">Tasks</h2>
        {event.tasks.length === 0 ? (
          <p className="text-base text-slate-600 dark:text-slate-400">No tasks yet.</p>
        ) : (
          <ul className="space-y-2 mb-4">
            {event.tasks.map((t) => (
              <li key={t.id} className="flex items-center gap-2.5 text-base">
                <button
                  data-testid={`task-toggle-${t.id}`}
                  onClick={() => doAction(() => api.post(`/events/${id}/tasks/${t.id}/toggle`), "Task updated")}
                  className="text-slate-600 dark:text-slate-400 hover:text-emerald-600 dark:hover:text-emerald-400 transition-colors"
                >
                  {t.status === "done" ? <CheckCircle className="h-4 w-4 text-emerald-600 dark:text-emerald-400" /> : <Circle className="h-4 w-4" />}
                </button>
                <span className={t.status === "done" ? "line-through text-slate-600 dark:text-slate-400" : "text-slate-700 dark:text-slate-300"}>{t.title}</span>
                {t.due_date && <span className="text-sm font-mono text-cyan-600 dark:text-cyan-400">{t.due_date}</span>}
              </li>
            ))}
          </ul>
        )}
        <form onSubmit={addTask} className="flex flex-wrap gap-2" data-testid="add-task-form">
          <input
            data-testid="task-title-input"
            value={taskTitle}
            onChange={(e) => setTaskTitle(e.target.value)}
            placeholder="Add a task…"
            className="flex-1 min-w-[180px] rounded-md border border-slate-300 dark:border-slate-700 bg-slate-100 dark:bg-slate-800 px-3 py-2 text-base text-slate-900 dark:text-slate-100 focus:border-emerald-500 dark:focus:border-emerald-500/60 focus:outline-none"
          />
          <input
            data-testid="task-due-input"
            type="date"
            value={taskDue}
            onChange={(e) => setTaskDue(e.target.value)}
            className="rounded-md border border-slate-300 dark:border-slate-700 bg-slate-100 dark:bg-slate-800 px-3 py-2 text-base text-slate-900 dark:text-slate-100 focus:border-emerald-500 dark:focus:border-emerald-500/60 focus:outline-none"
          />
          <button
            data-testid="task-add-btn"
            type="submit"
            className="inline-flex items-center gap-1.5 rounded-md bg-slate-200 dark:bg-slate-700 px-3 py-2 text-sm font-semibold text-slate-800 dark:text-slate-200 hover:bg-slate-700 transition-colors"
          >
            <Plus className="h-3.5 w-3.5" /> Add
          </button>
        </form>
      </div>
    </div>
  );
};

export default EventDetail;
